import { useCallback, useEffect, useState } from 'react';
import { callDashboardRpc, type DashboardRpc, type DashboardRpcArgs } from '../../api/dashboard';

interface RpcOptions {
  /**
   * true (the default): the last good result stays through later calls and
   * through failures, and an error stays until a call succeeds.
   * false: each new call (new args or a reload) starts from nothing, and a
   * result or error is never returned for args other than its own - for a
   * figure that must not show another period's numbers while it loads.
   */
  keepPrevious?: boolean;
  /** false makes no call; a call already in flight is dropped. Default true. */
  enabled?: boolean;
}

interface Settled<T> {
  data: T | null;
  error: string | null;
  /** The args the data or error belongs to. */
  key: string | null;
}

/**
 * Calls one RPC and keeps its last good result. No retry loop: retrying a
 * statement timeout (57014) only adds load to a database that is already
 * struggling, so a failure is shown with a Retry button instead. A response
 * that arrives after a newer call was made, or after unmount, is dropped.
 *
 * `name` and `args` are checked against types/supabase.ts (api/dashboard.ts);
 * `T` is the shape of the jsonb document the RPC returns.
 */
export function useRpc<T>(
  name: DashboardRpc,
  args: DashboardRpcArgs<DashboardRpc> | null,
  { keepPrevious = true, enabled = true }: RpcOptions = {},
): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const [settled, setSettled] = useState<Settled<T>>({ data: null, error: null, key: null });
  const [loading, setLoading] = useState(enabled);
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify(args);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    if (!keepPrevious) setSettled({ data: null, error: null, key });
    const params = key === 'null' ? undefined : (JSON.parse(key) as DashboardRpcArgs<DashboardRpc>);
    callDashboardRpc(name, params).then(({ data: result, error: rpcError }) => {
      if (cancelled) return;
      if (rpcError) {
        setSettled((prev) => ({ data: keepPrevious ? prev.data : null, error: rpcError.message, key }));
      } else {
        setSettled({ data: result as unknown as T, error: null, key });
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [name, key, attempt, keepPrevious, enabled]);

  const reload = useCallback(() => setAttempt((a) => a + 1), []);
  const own = keepPrevious || settled.key === key;
  return { data: own ? settled.data : null, error: own ? settled.error : null, loading, reload };
}

/** Scrolls to a section and moves focus there, so keyboard users land on it too. */
export function jumpTo(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.focus({ preventScroll: true });
}
